import { AccountUser, type Country, type DestinationPrices, type Resort } from '@travelata-libs/travelata-frontend';
import DepartureCitiesRepository from "@/js/common/Domain/Repository/DepartureCities";
import CountriesRepository from '@/js/common/Domain/Repository/Countries';
import { getPanelList, getCurrentCity } from '@entities/region-city';
import ResortsRepository from '@/js/common/Domain/Repository/Resorts';
import ResortFactory from '@/js/common/Domain/Factory/Resort';
import HotelsRepository from '@/js/common/Domain/Repository/Hotels';
import eventBus from '@ve/services/eventBus';
import account from '@/js/desktop/components/account';
import Vue, { reactive } from 'vue';
import type { Store } from 'vuex';
import Backbone from 'backbone';
import getStore from '@ve/services/getStore/getStore';
declare let window: any;

import type { HotelPageFlow } from '../config/hotelPageFlow';
import LibSearchFormHost from '../ui/LibSearchFormHost.vue';
import { IMapForSearchFormState } from '@fr/vue/pages/serp/store';

const HOST_ANCHOR_SELECTOR = '#js-lib-search-form-host';
const SSR_ROOT_SELECTOR = '[data-server-rendered="true"]';

interface SearchControllerLike {
    model: Backbone.Model;
    formSubmit(): void;
}

// Отельная — не вертикаль: одна страница с тремя режимами, режим приходит отдельно, в flow.
export type SerpVertical = 'tours' | 'hot' | 'hotels' | 'railway' | 'hotelPage';

export interface MountLibSearchFormOptions {
    store?: Store<unknown>;
    controller: SearchControllerLike;
    vertical?: SerpVertical;
    flow?: HotelPageFlow;
}

export interface MountLibSearchFormResult {
    unmount(): void;
}

interface LibSearchFormHostExpose {
    validate(): boolean;
}

export function mountLibSearchForm(options: MountLibSearchFormOptions): MountLibSearchFormResult {
    const { controller } = options;
    const store = options.store ?? getStore();
    const cities = getPanelList();
    const currentCity = getCurrentCity(cities);
    const authData = reactive<{ user: AccountUser | null, accountUrl: string; }>({
        user: null,
        accountUrl: window.appConfig.accountUrl,
    });
    const priceState = reactive<{ prices: DestinationPrices }>({
        prices: { country: {}, resort: {} },
    });

    const onAuthorize = (user: AccountUser) => {
        authData.user = user;
    };
    eventBus.$on('authorize', onAuthorize);

    const onMinPricesLoaded = ({ countries, resorts }: { countries: Backbone.Model<Country>[], resorts: Backbone.Model<Resort>[] }) => {
        const country: Record<number, number> = { ...priceState.prices.country };
        const resort: Record<number, number> = { ...priceState.prices.resort };
        countries.forEach((model) => {
            country[model.get('id')] = model.get('minPrice') || 0;
        });
        resorts.forEach((model) => {
            resort[model.get('id')] = model.get('minPrice') || 0;
        });
        priceState.prices = { country, resort };
    };

    const rootVm = new Vue({
        name: 'LibSearchFormHostRoot',
        store,
        render: (createElement) => createElement(LibSearchFormHost, {
            ref: 'host',
            props: {
                cities,
                currentCity,
                authData,
                prices: priceState.prices,
                vertical: options.vertical,
                flow: options.flow,
            },
            on: {
                search: () => onSearch(),

                ['update:modelValue']: (data: IMapForSearchFormState) => {
                    const departureCity = DepartureCitiesRepository.repository.get(data.departureCity.id);
                    let country = CountriesRepository.repository.get(data.countries[0].id);
                    const modelsHotel = HotelsRepository.repository.getHotelsBuild(data.hotels);
                    let resorts = [];
                    if(data.resorts.length) {
                        resorts = data.resorts.map((r) => new ResortFactory({ ...r, country: data.countries[0].id }));
                    }
                    if (data.countries.length && data.countries[0]?.resorts?.length) {
                        ResortsRepository.repository.addCountryResorts(data.countries[0].resorts, data.countries[0].id);
                    }
                    country = CountriesRepository.repository.get(data.countries[0].id);
                    controller.model.get('countries').set('model', new CountriesRepository([country.clone()]), { disabledSetResorts: true });
                    controller.model.get('resorts').set('model', new Backbone.Collection(resorts));
                    controller.model.get('hotels').set('model', modelsHotel);
                    controller.model.get('departureCity').set('model', departureCity);
                    controller.model.get('nightRange').set('model', new Backbone.Model(data.nightRange));
                    controller.model.get('touristGroup').set('model', new Backbone.Model(data.touristGroup));
                    controller.model.get('checkInDateRange').set('model', new Backbone.Model(data.checkInDateRange));
                },
                login: () => {
                    account.topMenuBar.openAuthForm();
                },
                logout: () => {
                    authData.user = null;
                    account.topMenuBar.logout();
                },
                buy: () => {
                    $('.openGeneralLeadForm')?.click();
                }
            },
        }),
    });

    function getHost(): LibSearchFormHostExpose | null {
        return (rootVm.$refs.host as unknown as LibSearchFormHostExpose | undefined) ?? null;
    }

    function onSearch(): void {
        const host = getHost();
        const isFormValid = host ? host.validate() : true;
        if (!isFormValid) {
            return;
        }
        controller.formSubmit();
    }
    const anchor = typeof document === 'undefined' ? null : document.querySelector(HOST_ANCHOR_SELECTOR);
    if (anchor) {
        CountriesRepository.repository.on('minPricesLoaded', onMinPricesLoaded);
        const ssrRoot = anchor.querySelector(SSR_ROOT_SELECTOR);
        rootVm.$mount((ssrRoot ?? anchor) as Element);
    }

    return {
        unmount(): void {
            CountriesRepository.repository.off('minPricesLoaded', onMinPricesLoaded);
            eventBus.$off('authorize', onAuthorize);
            rootVm.$destroy();
        },
    };
}
